<template>
  <div>
    <!-- Floating Action Button to open the chat sidebar -->
    <button
      v-show="!isOpen"
      type="button"
      class="chat-fab"
      aria-label="Open CRESiteIQ Assistant"
      @click="toggle"
    >
      <theme-icon icon="sparkles" type="icons" classes="w-5 h-5" />
    </button>

    <!-- Backdrop (mobile / focus aid) -->
    <transition name="chat-fade">
      <div v-if="isOpen" class="chat-backdrop" @click="close"></div>
    </transition>

    <!-- Sidebar panel -->
    <transition name="chat-slide">
      <aside
        v-if="isOpen"
        class="chat-sidebar"
        :class="{ 'chat-sidebar--rail-collapsed': railCollapsed }"
        role="dialog"
        aria-label="CRESiteIQ Assistant"
      >
        <header class="chat-sidebar__header">
          <div class="chat-sidebar__title">
            <theme-icon icon="sparkles" type="icons" classes="w-4 h-4" />
            <span>CRESiteIQ Assistant</span>
          </div>
          <button
            type="button"
            class="chat-sidebar__close"
            aria-label="Close chat"
            @click="close"
          >
            <theme-icon icon="close" type="icons" classes="w-4 h-4" />
          </button>
        </header>

        <div class="chat-sidebar__body">
          <chat-session-list
            :collapsed="railCollapsed"
            @toggle-collapse="railCollapsed = !railCollapsed"
          />

          <section class="chat-sidebar__main">
            <chat-message-list />
            <chat-input />
          </section>
        </div>
      </aside>
    </transition>
  </div>
</template>

<script>
/**
 * Top-level chat sidebar — owns the open/close transition and the
 * collapse state of the session rail. Mounted globally in the V1 Blade
 * layout (resources/views/theme/v1/layouts/app.blade.php) and gated
 * server-side by the `chatbot_enabled` flag, so this component only
 * exists in the DOM when the feature is on for the current client.
 */
export default {
  name: 'ChatSidebar',

  data() {
    return {
      railCollapsed: false,
    };
  },

  computed: {
    isOpen() {
      return this.$store.state.chat.isSidebarOpen;
    },
  },

  watch: {
    isOpen(v) {
      // Lock the body when the sidebar is open on small screens to avoid
      // double-scrolling jitter, and unlock it on close.
      if (v) {
        document.body.classList.add('chat-sidebar-open');
      } else {
        document.body.classList.remove('chat-sidebar-open');
      }
    },
  },

  mounted() {
    // ESC closes the sidebar — wired via document listener so it works
    // even when focus is in a textarea inside the sidebar.
    this._onKey = (e) => {
      if (e.key === 'Escape' && this.isOpen) this.close();
    };
    document.addEventListener('keydown', this._onKey);
  },

  beforeDestroy() {
    if (this._onKey) {
      document.removeEventListener('keydown', this._onKey);
    }
    document.body.classList.remove('chat-sidebar-open');
  },

  methods: {
    toggle() {
      this.$store.dispatch('chat/toggleSidebar');
    },
    close() {
      this.$store.dispatch('chat/closeSidebar');
    },
  },
};
</script>
